import { cn } from '@mntn-dev/ui-utilities'

import {
  type ThemeHeight,
  getHeightClassName,
  themeBackgroundMap,
  themeBorderColorMap,
  themeDisabledBorderColorMap,
  themeDisabledTextColorMap,
  themeFocusBorderColorMap,
  themeHoverBorderColorMap,
  themePlaceholderTextColorMap,
  themeReadonlyBorderColorMap,
  themeTextColorMap,
} from '@mntn-dev/ui-theme'
import type { UseFormFieldProps } from '../form-field/use-form-field.ts'

export type GetInputStylesOptions = Pick<
  UseFormFieldProps,
  'disabled' | 'readOnly' | 'hasError' | 'hasSuccess' | 'hasWarning'
> &
  ThemeHeight

export const getInputStyles = ({
  disabled,
  readOnly,
  hasError,
  hasSuccess,
  hasWarning,
  height = '12',
  minHeight,
  maxHeight,
}: GetInputStylesOptions = {}) => {
  const borderOverridden =
    hasError || hasSuccess || hasWarning || disabled || readOnly

  const heightClassName = getHeightClassName({ height, minHeight, maxHeight })

  return cn(
    'flex w-full leading-6 rounded-sm text-base p-3 outline-hidden font-medium items-center self-stretch justify-start disabled:placeholder:invisible aria-readonly:placeholder:invisible',
    heightClassName,
    themeTextColorMap.primary,
    themeBackgroundMap['container-secondary'],
    themePlaceholderTextColorMap.primary,
    themeDisabledTextColorMap.primary,
    themeDisabledBorderColorMap.muted,
    themeReadonlyBorderColorMap.muted,
    {
      border: !readOnly,
      'shadow-elevation-xs': !(readOnly || disabled),
      [themeBackgroundMap['container-tertiary']]: readOnly,
      [cn(
        themeBorderColorMap.muted,
        themeHoverBorderColorMap.muted,
        themeFocusBorderColorMap.muted
      )]: !borderOverridden,
      [cn(themeBorderColorMap.positive, themeFocusBorderColorMap.positive)]:
        hasSuccess,
      [cn(themeBorderColorMap.negative, themeFocusBorderColorMap.negative)]:
        hasError,
      [cn(themeBorderColorMap.caution, themeFocusBorderColorMap.caution)]:
        hasWarning,
    }
  )
}

export function getPlainTextInputStyles(invalid?: boolean) {
  const sharedClasses =
    'bg-transparent outline-0 ring-0 focus:outline-hidden focus:border focus:ring-0 focus:ring-offset-0'
  if (invalid) {
    return `border border-primary-red focus:border-primary-red ${sharedClasses}`
  }
  return `border-0 focus:border-none ${sharedClasses} `
}
